import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { api } from '../api';
import { Plus, Lock, Unlock, Shield, User, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', role: 'agent' });
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
        setLoading(true);
        setError(null);

        try {
            const usersRes = await api.get('/users/');
            setUsers(usersRes.data);
        } catch (e) {
            console.error("Failed to fetch users", e);
            setError("Could not load users. You might not have permission.");
        }
    } catch (err) {
      console.error(err);
      setError("An unexpected error occurred.");
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateUser = async () => {
      try {
          await api.post('/users/', newUser);
          toast.success("User created");
          setIsUserModalOpen(false);
          setNewUser({ email: '', name: '', password: '', role: 'agent' });
          fetchData();
      } catch (err) {
          toast.error(err.response?.data?.detail || "Failed to create user");
      }
  };

  const toggleUserBlock = async (userId, currentStatus) => {
      const targetUser = users.find(u => u.id === userId || u.user_id === userId);
      if (targetUser?.email === "jon@atcoastal.com") {
          toast.error("Cannot block Main Admin.");
          return;
      }

      try {
          await api.put(`/users/${userId}/block`, { blocked: !currentStatus });
          toast.success(currentStatus ? "User unblocked" : "User blocked");
          fetchData();
      } catch (err) {
          toast.error("Failed to update user status");
      }
  };

  const changeUserRole = async (userId, newRole) => {
      const targetUser = users.find(u => u.id === userId || u.user_id === userId);
      
      if (targetUser?.email === "jon@atcoastal.com" && newRole !== 'admin') {
          toast.error("Main Admin cannot be downgraded.");
          return;
      }

      try {
          await api.put(`/users/${userId}`, { role: newRole });
          toast.success(`Role updated to ${newRole}`);
          fetchData();
      } catch (err) {
          toast.error("Failed to update role");
      }
  };

  if (loading) {
      return (
        <div className="h-full flex items-center justify-center p-10">
            <div className="text-white/50 animate-pulse">Loading Users...</div>
        </div>
      );
  }

  if (error) {
      return (
        <div className="h-full flex items-center justify-center p-10">
            <div className="text-red-400 bg-red-500/10 p-4 rounded-xl border border-red-500/20">
                {error}
                <Button variant="link" onClick={fetchData} className="text-red-300 ml-2">Retry</Button>
            </div>
        </div>
      );
  }

  return (
    <div className="h-full overflow-auto p-6 md:p-10 max-w-7xl mx-auto space-y-8 rounded-3xl lg-glass">
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-1">
            <h1 className="text-3xl font-bold tracking-tight text-white">User Management</h1>
            <p className="text-white/50">Manage access, roles, and account status.</p>
        </div>
        <Button onClick={() => setIsUserModalOpen(true)} className="bg-cyan-500 text-black hover:bg-cyan-400 gap-2">
            <Plus className="h-4 w-4" /> Create User
        </Button>
      </div>

      <Card className="lg-glass border-white/10 text-white bg-transparent">
        <CardContent className="p-0">
            <Table>
                <TableHeader className="bg-white/5 border-b border-white/10">
                    <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-white/60">Name</TableHead>
                        <TableHead className="text-white/60">Email</TableHead>
                        <TableHead className="text-white/60">Role</TableHead>
                        <TableHead className="text-white/60">Status</TableHead>
                        <TableHead className="text-white/60 text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {users.map((user) => {
                        const isMainAdmin = user.email === "jon@atcoastal.com";
                        const id = user.id || user.user_id;
                        
                        return (
                        <TableRow key={id} className="border-white/5 hover:bg-white/5 transition-colors group">
                            <TableCell className="font-medium">
                                <div className="flex items-center gap-3">
                                    <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                                        {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : <User className="h-4 w-4 text-white/50" />}
                                    </div>
                                    {user.name}
                                </div>
                            </TableCell>
                            <TableCell className="text-white/70">{user.email}</TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <Badge variant="outline" className={`text-[10px] ${user.role === 'admin' ? 'border-cyan-500/50 text-cyan-400' : 'border-white/20 text-white/60'}`}>
                                        {user.role}
                                    </Badge>
                                    
                                    {!isMainAdmin && (
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                            {user.role === 'agent' ? (
                                                <Button 
                                                    variant="ghost" size="icon" className="h-6 w-6 text-white/30 hover:text-cyan-400"
                                                    title="Promote to Admin"
                                                    onClick={() => changeUserRole(id, 'admin')}
                                                >
                                                    <Shield className="h-3 w-3" />
                                                </Button>
                                            ) : (
                                                <Button 
                                                    variant="ghost" size="icon" className="h-6 w-6 text-white/30 hover:text-white"
                                                    title="Demote to Agent"
                                                    onClick={() => changeUserRole(id, 'agent')}
                                                >
                                                    <UserCheck className="h-3 w-3" />
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </TableCell>
                            <TableCell>
                                {user.status === 'blocked' || user.blocked ? (
                                    <Badge variant="destructive" className="text-[10px]">Blocked</Badge>
                                ) : user.status === 'banned' ? (
                                    <Badge variant="destructive" className="text-[10px]">Banned</Badge>
                                ) : (
                                    <Badge variant="outline" className="border-green-500/30 text-green-400 text-[10px]">Active</Badge>
                                )}
                            </TableCell>
                            <TableCell className="text-right">
                                {!isMainAdmin && (
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        onClick={() => toggleUserBlock(id, user.blocked)}
                                        className={user.blocked ? "text-green-400 hover:text-green-300 gap-1" : "text-red-400 hover:text-red-300 gap-1"}
                                    >
                                        {user.blocked ? <><Unlock className="h-3 w-3" /> Unblock</> : <><Lock className="h-3 w-3" /> Block</>}
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                    )})}
                </TableBody>
            </Table>
        </CardContent>
      </Card>

      <Dialog open={isUserModalOpen} onOpenChange={setIsUserModalOpen}>
        <DialogContent className="lg-glass-strong border-white/10 text-white">
            <DialogHeader>
                <DialogTitle>Create User</DialogTitle>
                <DialogDescription>Manually add a new agent user.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
                <div className="space-y-2">
                    <Label>Email</Label>
                    <Input 
                        value={newUser.email} 
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        className="bg-black/20 border-white/10 text-white" 
                    />
                </div>
                <div className="space-y-2">
                    <Label>Name</Label>
                    <Input 
                        value={newUser.name} 
                        onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                        className="bg-black/20 border-white/10 text-white" 
                    />
                </div>
                <div className="space-y-2">
                    <Label>Password</Label>
                    <Input 
                        type="password"
                        value={newUser.password} 
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        className="bg-black/20 border-white/10 text-white" 
                    />
                </div>
                <div className="space-y-2">
                    <Label>Role</Label>
                    <Select 
                        value={newUser.role} 
                        onValueChange={(val) => setNewUser({...newUser, role: val})}
                    >
                        <SelectTrigger className="bg-black/20 border-white/10 text-white">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="lg-glass-strong border-white/10 text-white">
                            <SelectItem value="agent">Agent</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <DialogFooter>
                <Button variant="ghost" onClick={() => setIsUserModalOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateUser} className="bg-cyan-500 text-black hover:bg-cyan-400">Create</Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
